import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, HardHat, Phone, MapPin, Briefcase,
  CheckCircle2, Clock, Loader2, Search, ChevronRight,
  ChevronLeft, Package, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';
import type { SellerInstaller } from '@m8-app/contracts';
import { cn } from '../components/ui/cn.js';
import { listInstallers, createInstaller, getSellerDevices } from '../api/seller.js';
import type { CreateInstallerBodyV2 } from '../api/seller.js';
import { extractErrorMessage } from '../lib/errors.js';

const PAGE_SIZE = 10;

function fmt(iso: string) {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 shadow-sm">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={cn('text-2xl font-bold mt-1', color)}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

/* ── list view ── */
function InstallerListView() {
  const navigate   = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);

  const [newName, setNewName]             = useState('');
  const [newMobile, setNewMobile]         = useState('');
  const [newArea, setNewArea]             = useState('');
  const [newExperience, setNewExperience] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['seller-installers'],
    queryFn:  listInstallers,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateInstallerBodyV2) => createInstaller(body),
    onSuccess: () => {
      toast.success('Installer added successfully.');
      qc.invalidateQueries({ queryKey: ['seller-installers'] });
      setShowAddModal(false);
      setNewName(''); setNewMobile(''); setNewArea(''); setNewExperience('');
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Failed to add installer.')),
  });

  const installers = data?.installers ?? [];

  const filtered = installers.filter((i) => {
    const q = search.toLowerCase();
    return q === ''
      || i.name.toLowerCase().includes(q)
      || i.mobile.includes(q)
      || (i.serviceArea ?? '').toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleAdd() {
    if (!newName.trim() || !newMobile.trim()) {
      toast.error('Name and mobile are required.'); return;
    }
    const area = newArea.trim();
    const exp = newExperience.trim();
    createMutation.mutate({
      name:   newName.trim(),
      mobile: newMobile.trim(),
      ...(area ? { serviceArea: area } : {}),
      ...(exp ? { experience: exp } : {}),
    });
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Installers</h1>
          <p className="text-sm text-slate-500 mt-0.5">All installers associated with your registered devices.</p>
        </div>
        <button type="button" onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg transition-all shadow-sm shrink-0">
          <Plus className="w-3.5 h-3.5" />
          Add Installer
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name, mobile or area…"
          className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80 border-b border-slate-100">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-10">#</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Installer</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Service Area</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Experience</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Added</th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? [0, 1, 2].map((i) => (
                <tr key={i} className="border-b border-slate-50">
                  {[0, 1, 2, 3, 4, 5].map((j) => (
                    <td key={j} className="px-5 py-3.5"><div className="h-4 bg-slate-100 rounded animate-pulse w-20" /></td>
                  ))}
                </tr>
              ))
              : paged.map((inst, i) => (
              <tr
                key={inst.id}
                className={cn(
                  'transition-colors',
                  i < paged.length - 1 && 'border-b border-slate-50',
                )}
              >
                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">
                  {(page - 1) * PAGE_SIZE + i + 1}
                </td>
                <td className="px-5 py-3.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/installers?id=${inst.id}`)}
                    className="flex items-center gap-3 text-left hover:opacity-80 transition-opacity"
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                      <HardHat className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-blue-600 underline-offset-2 hover:underline">{inst.name}</p>
                      {inst.experience && <p className="text-xs text-slate-400">{inst.experience}</p>}
                    </div>
                  </button>
                </td>
                <td className="px-5 py-3.5 text-sm text-slate-600">{inst.mobile}</td>
                <td className="px-5 py-3.5">
                  {inst.serviceArea ? (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                      <MapPin className="w-3 h-3 text-slate-400" />{inst.serviceArea}
                    </span>
                  ) : <span className="text-xs text-slate-300">—</span>}
                </td>
                <td className="px-5 py-3.5 text-xs text-slate-500">{inst.experience ?? '—'}</td>
                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">{fmt(inst.createdAt)}</td>
              </tr>
            ))}
            {!isLoading && paged.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">No installers found.</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">
            {filtered.length === 0 ? '0 results' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors">
              <ChevronLeft className="w-4 h-4 text-slate-500" />
            </button>
            <span className="px-2.5 text-xs font-semibold text-slate-600 tabular-nums">{page}/{totalPages}</span>
            <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors">
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-slate-900">Add Installer</h2>
              <button type="button" onClick={() => setShowAddModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="installer-name" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Full Name *</label>
                <input id="installer-name" type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="Installer full name" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
              </div>
              <div>
                <label htmlFor="installer-mobile" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Mobile *</label>
                <input id="installer-mobile" type="tel" value={newMobile} onChange={(e) => setNewMobile(e.target.value)}
                  placeholder="+91 98765 43210" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
              </div>
              <div>
                <label htmlFor="installer-area" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Service Area</label>
                <input id="installer-area" type="text" value={newArea} onChange={(e) => setNewArea(e.target.value)}
                  placeholder="City or region" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
              </div>
              <div>
                <label htmlFor="installer-experience" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Experience</label>
                <input id="installer-experience" type="text" value={newExperience} onChange={(e) => setNewExperience(e.target.value)}
                  placeholder="e.g. 5 years" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={() => setShowAddModal(false)}
                className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                Cancel
              </button>
              <button type="button" onClick={handleAdd} disabled={createMutation.isPending}
                className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-all inline-flex items-center justify-center gap-2">
                {createMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {createMutation.isPending ? 'Adding…' : 'Add Installer'}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">Installer will be available to select when registering devices.</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── profile view ── */
function InstallerProfileView({ id }: { id: string }) {
  const navigate = useNavigate();

  const { data: installersData } = useQuery({ queryKey: ['seller-installers'], queryFn: listInstallers });
  const { data: devicesData }    = useQuery({ queryKey: ['seller-devices'],    queryFn: getSellerDevices });

  const installer: SellerInstaller | undefined = installersData?.installers.find((i) => i.id === id);

  // Find devices that have this installer's mobile
  const installerDevices = (devicesData?.registrations ?? []).filter(
    (r) => installer && (r.installerMobile === installer.mobile || r.installerName === installer.name),
  );

  if (!installer) {
    return (
      <div className="p-6">
        <button type="button" onClick={() => navigate('/installers')}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to Installers
        </button>
        <p className="text-sm text-slate-400">Installer not found.</p>
      </div>
    );
  }

  const completedCount = installerDevices.filter((d) => d.status === 'activated' || d.status === 'claimed').length;
  const pendingCount   = installerDevices.filter((d) => d.status === 'pending').length;

  return (
    <div className="p-6 max-w-4xl">
      <button type="button" onClick={() => navigate('/installers')}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors mb-6 -ml-0.5">
        <ArrowLeft className="w-4 h-4" /> Back to Installers
      </button>

      {/* Header */}
      <div className="flex items-start gap-5 mb-6">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 border-2 border-amber-200 flex items-center justify-center shrink-0">
          <HardHat className="w-8 h-8 text-amber-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{installer.name}</h1>
          <div className="flex items-center gap-4 mt-1.5 flex-wrap">
            <span className="flex items-center gap-1.5 text-sm text-slate-500">
              <Phone className="w-3.5 h-3.5" />{installer.mobile}
            </span>
            {installer.serviceArea && (
              <span className="flex items-center gap-1.5 text-sm text-slate-500">
                <MapPin className="w-3.5 h-3.5" />{installer.serviceArea}
              </span>
            )}
            {installer.experience && (
              <span className="flex items-center gap-1.5 text-sm text-slate-500">
                <Briefcase className="w-3.5 h-3.5" />{installer.experience} experience
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1">Added {fmt(installer.createdAt)}</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard label="Total Devices"     value={installerDevices.length} color="text-slate-900" />
        <StatCard label="Activated/Claimed" value={completedCount}          color="text-emerald-600" sub="installations" />
        <StatCard label="Pending"           value={pendingCount}            color="text-amber-600"   sub="in progress" />
      </div>

      {/* Installation history table */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Installation History</h2>
        </div>

        {installerDevices.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-6 py-10 text-center">
            <p className="text-sm text-slate-400">No installations recorded yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80 border-b border-slate-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">#</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Serial Number</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Customer</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Registered On</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {installerDevices.map((d, i) => (
                  <tr
                    key={d.registrationId}
                    onClick={() => navigate(`/devices/${d.registrationId}`)}
                    className={cn(
                      'cursor-pointer hover:bg-blue-50/30 transition-colors',
                      i < installerDevices.length - 1 && 'border-b border-slate-50',
                    )}
                  >
                    <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">{i + 1}</td>
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-sm font-semibold text-blue-600">{d.serialNumber}</span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-slate-500">{d.customerMobile}</td>
                    <td className="px-5 py-3.5 text-xs text-slate-500 tabular-nums">{fmt(d.registeredAt)}</td>
                    <td className="px-5 py-3.5">
                      {d.status === 'activated' || d.status === 'claimed' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">
                          <CheckCircle2 className="w-3 h-3" /> Activated
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 ring-1 ring-amber-200/60">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── main export ── */
export function InstallersPage() {
  const [params] = useSearchParams();
  const id       = params.get('id');

  if (id) return <InstallerProfileView id={id} />;
  return <InstallerListView />;
}
